import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy, EventEmitter, Output } from '@angular/core';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';

export enum ResizeDirectionEnum {
    none = 'none',
    both = 'both',
    horizontal = 'horizontal',
    vertical = 'vertical'
}

/**
 * ### Accessibility Usage
 * + ***ariaLabel*** - use this to provide an accessible label for the text area
 * + ***ariaDescribedBy*** - use this if the text area has an external error message, or it needs extra description for screen reader. Use message's id
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***initialFocus*** - set to true to make this input have focus when it is created
 */
@Component({
    selector: 'agpr-textarea-input',
    templateUrl: './textarea-input.component.html',
    styleUrls: ['./textarea-input.component.scss'],
    providers: [getProvider(TextareaInputComponent)],
    standalone: false
})
export class TextareaInputComponent extends ControlValueAccessorModel<string> implements AfterViewInit, OnDestroy {
    @Input() public id: string;
    @Input() public isEditing: boolean = true;
    @Input() public label: string;
    @Input() public placeholder: string;
    @Input() public ariaLabel: string;
    @Input() public required: boolean = false;
    @Input() public disabled: boolean = false;
    @Input() public autoGrow: boolean = false;
    @Input() public maxLength: number;
    @Input() public initialFocus: boolean = false;
    @Input() public bottomMargin: boolean = true;
    @Input() public icon: string;
    @Input() public tooltip: string;
    @Input() public hint: string;

    @Input() public rows: number = 2;
    @Input() public resize: ResizeDirectionEnum = ResizeDirectionEnum.vertical;
    @Input() public style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;

    @Input() public agentic: boolean = false;

    @Input() public ariaDescribedBy: string;

    // `change`, `keyup` and `keydown` are handled automatically by angular + browser
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    @ViewChild('input') input: ElementRef;
    @ViewChild('contentEditableDiv') contentEditableDiv: ElementRef;

    public focused: boolean = false;
    public isDomEmpty: boolean = true;
    private _enteredLineBreak = false;
    private readonly contentEditableCursorAnchor = '\u200B';
    readonly componentStyles = AgprComponentStyleEnum;
    readonly resizeNone = ResizeDirectionEnum.none;

    // Use rAF so we run after Chrome finalises cursor placement on click/focus.
    private selectionChangeHandler = (): void => {
        requestAnimationFrame(() => this.ensureCursorAfterIcon());
    };

    get contentEditableMinHeight(): string {
        // rows * line-height (1.5em) + top/bottom padding (0.25em each)
        return `calc(${this.rows} * 1.5em + 0.5em)`;
    }

    override writeValue(value: string): void {
        super.writeValue(value);
        if (this.contentEditableDiv && this.icon) {
            this.updateContentEditable(value ?? '');
        }
    }

    ngAfterViewInit(): void {
        if (this.icon && this.contentEditableDiv) {
            if (this.value) {
                this.updateContentEditable(this.value);
            }
            document.addEventListener('selectionchange', this.selectionChangeHandler);
        }
        if (this.initialFocus) {
            this.giveFocus();
        }
    }

    ngOnDestroy(): void {
        document.removeEventListener('selectionchange', this.selectionChangeHandler);
    }

    private ensureCursorAfterIcon(): void {
        if (!this.contentEditableDiv) {
            return;
        }
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const iconSpan = el.querySelector('span[contenteditable="false"]');
        if (!iconSpan) {
            return;
        }
        const cursorAnchor = this.ensureCursorAnchor(el, iconSpan);
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }
        const range = selection.getRangeAt(0);
        if (!el.contains(range.startContainer)) {
            return;
        }
        if (!range.collapsed) {
            // User clicked directly on the icon — it gets selected as an object; collapse after it
            if (range.intersectsNode(iconSpan)) {
                this.collapseAfterIcon(selection, iconSpan);
            }
            return;
        }
        // Collapsed cursor landed before the icon — move it to just after the icon
        if (this.isRangeBeforeCursorAnchor(range, el, cursorAnchor)) {
            this.collapseAfterCursorAnchor(selection, cursorAnchor);
        }
    }

    private collapseAfterIcon(selection: Selection, iconSpan: Element): void {
        if (!iconSpan.parentElement) {
            return;
        }
        const cursorAnchor = this.ensureCursorAnchor(iconSpan.parentElement, iconSpan);
        this.collapseAfterCursorAnchor(selection, cursorAnchor);
    }

    private collapseAfterCursorAnchor(selection: Selection, cursorAnchor: Text): void {
        const newRange = document.createRange();
        newRange.setStart(cursorAnchor, this.contentEditableCursorAnchor.length);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
    }

    giveFocus() {
        const focusTarget = this.contentEditableDiv?.nativeElement ?? this.input?.nativeElement;
        if (focusTarget) {
            requestAnimationFrame(() => focusTarget.focus());
        }
    }

    onFocus(event) {
        this.focus.emit(event);
        this.focused = true;
        if (this.icon && this.contentEditableDiv) {
            requestAnimationFrame(() => {
                const el = this.contentEditableDiv.nativeElement as HTMLElement;
                if (this.isDomEmpty) {
                    this.removeNonIconNodes(el);
                }
                const sel = window.getSelection();
                const iconSpan = el.querySelector('span[contenteditable="false"]') as Element;
                if (!sel || !iconSpan) {
                    return;
                }
                const cursorIsInsideDiv = sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).startContainer);
                if (!cursorIsInsideDiv) {
                    // Tab/keyboard focus: browser doesn't set a cursor in contenteditable —
                    // place it explicitly after the icon.
                    this.collapseAfterIcon(sel, iconSpan);
                } else {
                    this.ensureCursorAfterIcon();
                }
            });
        }
    }

    onBlur(event) {
        this.blur.emit(event);
        this.focused = false;
        this.onModelTouched();
        if (this.icon && this.contentEditableDiv && !this.value) {
            // Clean up any trailing <br> left by Enter in an empty field so
            // the placeholder is on the first line the next time focus arrives.
            this.removeNonIconNodes(this.contentEditableDiv.nativeElement);
            this.isDomEmpty = true;
        }
    }

    onContentEditableClick(): void {
        if (this.icon) {
            this.ensureCursorAfterIcon();
        }
    }

    adjustHeight(el){
        this.input.nativeElement.style.height = 'auto';
        if (this.autoGrow) {
            this.input.nativeElement.style.height = (el.target.scrollHeight > el.target.clientHeight) ? el.target.scrollHeight + 'px' : 'auto';
        }
    }

    onContentEditableKeyDown(event: KeyboardEvent): void {
        if ((event.key === 'a' || event.key === 'A') && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            const el = this.contentEditableDiv.nativeElement as HTMLElement;
            const iconSpan = el.querySelector('span[contenteditable=false]');
            const range = document.createRange();
            if (iconSpan) {
                range.setStartAfter(iconSpan);
            } else {
                range.setStart(el, 0);
            }
            range.setEnd(el, el.childNodes.length);
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
            }
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            // Prevent Enter when cursor is before or over icon (would push icon to new line)
            if (!this.cursorIsBeforeOrOverIcon()) {
                this._enteredLineBreak = true;
                document.execCommand('insertLineBreak');
            }
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
            if (this.deleteContentEditableSelection()) {
                event.preventDefault();
            } else if (this.wouldAffectIcon(event.key)) {
                event.preventDefault();
            }
        }
        this.keydown.emit(event);
    }

    /** Returns true when the cursor or selection is before or overlaps the icon span. */
    private cursorIsBeforeOrOverIcon(): boolean {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return false;
        }
        const range = selection.getRangeAt(0);
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const iconSpan = el.querySelector('span[contenteditable="false"]');
        if (!iconSpan) {
            return false;
        }
        const cursorAnchor = this.ensureCursorAnchor(el, iconSpan);
        // Selection overlaps the icon
        if (!range.collapsed && range.intersectsNode(iconSpan)) {
            return true;
        }
        // Collapsed cursor is at offset 0 in the div (before the icon, its first child)
        return this.isRangeBeforeCursorAnchor(range, el, cursorAnchor);
    }

    /** Returns true when the pending Backspace or Delete would remove the icon span. */
    private wouldAffectIcon(key: string): boolean {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return false;
        }
        const range = selection.getRangeAt(0);
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const iconSpan = el.querySelector('span[contenteditable="false"]');
        if (!iconSpan) {
            return false;
        }
        const cursorAnchor = this.ensureCursorAnchor(el, iconSpan);

        // Non-collapsed selection that includes the icon
        if (!range.collapsed && (range.intersectsNode(iconSpan) || range.intersectsNode(cursorAnchor))) {
            return true;
        }

        if (range.collapsed) {
            if (key === 'Backspace') {
                // Cursor is in the div right after the icon (e.g. div[offset=1] when icon is childNodes[0])
                if (range.startContainer === el && range.startOffset === 1 && el.childNodes[0] === iconSpan) {
                    return true;
                }
                // Cursor is at start of the first text node, immediately after the icon
                if (range.startOffset === 0 && range.startContainer !== el) {
                    return range.startContainer.previousSibling === iconSpan || range.startContainer.previousSibling === cursorAnchor;
                }
                if (range.startContainer === cursorAnchor && range.startOffset <= this.contentEditableCursorAnchor.length) {
                    return true;
                }
            }
            if (key === 'Delete') {
                // Cursor is before the icon (at offset 0 in the container div)
                if (range.startContainer === el && range.startOffset === 0) {
                    return el.childNodes[0] === iconSpan;
                }
                if (range.startContainer === cursorAnchor && range.startOffset < this.contentEditableCursorAnchor.length) {
                    return true;
                }
            }
        }

        return false;
    }

    private deleteContentEditableSelection(): boolean {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
            return false;
        }

        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const range = selection.getRangeAt(0);
        if (!el.contains(range.commonAncestorContainer)) {
            return false;
        }

        const iconSpan = el.querySelector('span[contenteditable="false"].agpr-textareaInput-icon');
        if (!iconSpan) {
            return false;
        }
        const cursorAnchor = this.ensureCursorAnchor(el, iconSpan);
        if (!range.intersectsNode(iconSpan) && !range.intersectsNode(cursorAnchor)) {
            return false;
        }

        range.deleteContents();
        this.restoreIconIfMissing();
        this.updateValueFromContentEditable();

        const restoredIconSpan = el.querySelector('span[contenteditable="false"].agpr-textareaInput-icon');
        if (restoredIconSpan) {
            this.collapseAfterIcon(selection, restoredIconSpan);
        }
        return true;
    }

    onContentEditableChange(event: Event): void {
        this.restoreIconIfMissing();
        this.updateValueFromContentEditable();
    }

    private updateValueFromContentEditable(): void {
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const enteredLineBreak = this._enteredLineBreak;
        this._enteredLineBreak = false;
        const rawText = this.extractContentEditableText(el);
        const text = rawText.replace(/\n+$/, '');
        if (!text && !enteredLineBreak) {
            // All real text is gone and the user didn't just press Enter:
            // remove residual <br> nodes (e.g. Chrome auto-inserts one when the
            // last character is deleted) so the placeholder renders on the first line.
            this.removeNonIconNodes(el);
        }
        // Suppress the placeholder only when the user deliberately pressed Enter
        // in an empty field (cursor should stay on the new line, not jump back).
        this.isDomEmpty = !text && !enteredLineBreak;
        this.value = text;
        this.onModelChange(text);
    }

    private removeNonIconNodes(el: HTMLElement): void {
        const iconSpan = el.querySelector('span[contenteditable="false"]');
        Array.from(el.childNodes)
            .filter(n => n !== iconSpan)
            .forEach(n => el.removeChild(n));
        if (iconSpan) {
            this.ensureCursorAnchor(el, iconSpan);
        }
    }

    private restoreIconIfMissing(): void {
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        const iconSpan = el.querySelector('span[contenteditable="false"].agpr-textareaInput-icon');
        if (!iconSpan) {
            const span = document.createElement('span');
            span.setAttribute('contenteditable', 'false');
            span.className = `agpr-textareaInput-icon ${this.icon}`;
            el.insertBefore(span, el.firstChild);
        }
        const restoredIconSpan = el.querySelector('span[contenteditable="false"].agpr-textareaInput-icon');
        if (restoredIconSpan) {
            this.ensureCursorAnchor(el, restoredIconSpan);
        }
    }

    onContentEditablePaste(event: ClipboardEvent): void {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') || '';
        document.execCommand('insertText', false, text);
    }

    private extractContentEditableText(el: HTMLElement): string {
        let text = '';
        el.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += (node.textContent || '').replace(new RegExp(this.contentEditableCursorAnchor, 'g'), '');
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const elem = node as HTMLElement;
                if (elem.getAttribute('contenteditable') === 'false') {
                    return; // skip icon span
                }
                if (elem.tagName === 'BR') {
                    text += '\n';
                } else {
                    text += elem.innerText || elem.textContent || '';
                }
            }
        });
        return text;
    }

    private updateContentEditable(value: string): void {
        const el = this.contentEditableDiv.nativeElement as HTMLElement;
        this.removeNonIconNodes(el);
        if (value) {
            el.appendChild(document.createTextNode(value));
        }
        this.isDomEmpty = !value;
    }

    private ensureCursorAnchor(el: HTMLElement, iconSpan: Element): Text {
        const nextSibling = iconSpan.nextSibling;
        if (nextSibling?.nodeType === Node.TEXT_NODE && nextSibling.textContent?.startsWith(this.contentEditableCursorAnchor)) {
            return nextSibling as Text;
        }

        const cursorAnchor = document.createTextNode(this.contentEditableCursorAnchor);
        el.insertBefore(cursorAnchor, nextSibling);
        return cursorAnchor;
    }

    private getChildNodeOffset(el: HTMLElement, node: Node): number {
        return Array.prototype.indexOf.call(el.childNodes, node);
    }

    private isRangeBeforeCursorAnchor(range: Range, el: HTMLElement, cursorAnchor: Text): boolean {
        return range.collapsed && (
            (range.startContainer === el && range.startOffset <= this.getChildNodeOffset(el, cursorAnchor)) ||
            (range.startContainer === cursorAnchor && range.startOffset === 0)
        );
    }
}
